import { FC, useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Check, CheckSquare, Plus, X } from 'lucide-react';
import useGetUsersBySite from '../../../custom-hooks/useGetUsersBySite';
import { allGrades } from '../../../graphQLOperations/Classes';
import { ClassesAllGradesQuery } from '../../../graphQLOperations/__generated__/ClassesAllGradesQuery.graphql';
import { Grade } from '../../../models/Grade';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import { HELP_CENTER_URL } from '../ClassesRosterEmptyState';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { MaskedInput } from '../../MaskedData/MaskedInput';
import { PiiTypes } from '../../../analytics/dataMasking';
import { WizardUser } from './wizardTypes';

const STUDENT_ROLE_ID = 'RoleNode:2';
const PAGE_SIZE = 25;

interface Props {
  siteId: string;
  selected: WizardUser[];
  onChange: (students: WizardUser[]) => void;
  onNewStudent?: () => void;
}

const ClassWizardStudentsStep: FC<Props> = ({ siteId, selected, onChange, onNewStudent }) => {
  const [search, setSearch] = useState('');
  const [grades, setGrades] = useState<Grade[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { completeUsers, loading } = useGetUsersBySite({ roleIds: [STUDENT_ROLE_ID] });
  const gradesData = useLazyLoadQuery<ClassesAllGradesQuery>(allGrades, {});

  // Same grade options as the class details step (1-12), not just grades present in the roster.
  const gradeOptions = useMemo(
    () => [...gradesData.allGradeLevels].sort((a, b) => a.order - b.order) as Grade[],
    [gradesData],
  );

  const roster = useMemo(() => {
    const fromSite = completeUsers
      .filter(user => user.site?.some(site => site.id === siteId))
      .map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        gradeName: user.grade?.name ?? '',
        publicUserId: user.publicUserId,
      }));
    // A student created in this session is selected immediately, but won't appear in
    // completeUsers until the roster query refetches, so merge them in directly and
    // surface them first so the person who just created them can see them right away.
    const newlyCreated = selected
      .filter(student => !fromSite.some(user => user.id === student.id))
      .map(student => ({ ...student, gradeName: student.gradeName ?? '', publicUserId: '' }));
    return [...newlyCreated, ...fromSite.sort((a, b) => a.lastName.localeCompare(b.lastName))];
  }, [completeUsers, siteId, selected]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const gradeNames = grades.map(g => g.name);
    return roster
      .filter(student => !gradeNames.length || gradeNames.includes(student.gradeName))
      .filter(student => !term || `${student.firstName} ${student.lastName}`.toLowerCase().includes(term));
  }, [roster, search, grades]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, grades]);

  const visible = filtered.slice(0, visibleCount);

  const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48 && visibleCount < filtered.length) {
      setVisibleCount(count => count + PAGE_SIZE);
    }
  };

  const toggle = (student: WizardUser, checked: boolean) => {
    onChange(checked ? [...selected, student] : selected.filter(s => s.id !== student.id));
  };

  const selectAll = () => {
    const byId = new Map(selected.map(s => [s.id, s]));
    filtered.forEach(student =>
      byId.set(student.id, { id: student.id, firstName: student.firstName, lastName: student.lastName }),
    );
    onChange(Array.from(byId.values()));
  };

  const clearAll = () => onChange([]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[20rem] tw-w-full">
        <ThreeDotLoader dark />
        <p className="tw-text-neutral-800 tw-font-semibold tw-mb-3 tw-text-xl">Loading students...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-md py-2">
      <div className="flex justify-content-between align-items-center gap-md">
        {selected.length > 0 ? (
          <span className="flex align-items-center gap-sm">
            <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100">
              <Check size={12} className="tw-text-[#007A54]" aria-hidden />
            </span>
            <span className="text-lg font-medium" style={{ color: '#393939' }}>
              {selected.length} selected student{selected.length === 1 ? '' : 's'}
            </span>
          </span>
        ) : (
          <span className="flex align-items-center gap-sm text-color-secondary">
            <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[5.33px] tw-bg-neutral-100">
              <Check size={12} className="tw-text-[#A6A6A6]" aria-hidden />
            </span>
            <span
              className="text-lg font-bold"
              style={{ fontFamily: "'Atkinson Hyperlegible', sans-serif", lineHeight: '1.5rem' }}
            >
              No selected students
            </span>
          </span>
        )}
        <Button label="Add new student" icon={<Plus size={14} />} outlined rounded onClick={onNewStudent} />
      </div>

      <div className="flex align-items-center gap-md">
        <IconField iconPosition="left" className="nc_classes-search flex-grow-1">
          <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
          <MaskedInput>
            <InputText
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search students..."
              className="w-full"
              aria-label="Search students"
            />
          </MaskedInput>
        </IconField>
        <MultiSelect
          value={grades}
          options={gradeOptions}
          optionLabel="name"
          dataKey="id"
          onChange={event => setGrades(event.value as Grade[])}
          placeholder="All grades"
          display="chip"
          filter={false}
          aria-label="Filter by grade"
          className="flex-shrink-0"
          style={{ width: '12rem' }}
        />
      </div>

      <div className="nc_wizard-list-box flex flex-column">
        <div className="flex justify-content-between align-items-center nc_wizard-list-box__actions">
          <Button
            label="Select all"
            icon={<CheckSquare size={14} />}
            link
            className="nc_wizard-list-box__action-btn"
            onClick={selectAll}
            disabled={!filtered.length}
          />
          <Button
            label="Clear all"
            icon={<X size={14} />}
            link
            className="nc_wizard-list-box__clear nc_wizard-list-box__action-btn"
            onClick={clearAll}
            disabled={!selected.length}
          />
        </div>
        {filtered.length ? (
          <ul className="nc_wizard-user-list" onScroll={handleListScroll}>
            {visible.map(student => {
              const checked = selected.some(s => s.id === student.id);
              return (
                <li key={student.id}>
                  <label
                    className={`flex align-items-center gap-md cursor-pointer nc_wizard-user-list__row${
                      checked ? ' nc_wizard-user-list__row--selected' : ''
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={event =>
                        toggle(
                          { id: student.id, firstName: student.firstName, lastName: student.lastName },
                          Boolean(event.checked),
                        )
                      }
                    />
                    <MaskedStudentData student={student} type={PiiTypes.FullName}>
                      {`${student.firstName} ${student.lastName}`}
                    </MaskedStudentData>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="nc_wizard-empty-list flex flex-column align-items-center justify-content-center text-center text-color-secondary">
            {search ? (
              `No match for "${search}"`
            ) : (
              <span>
                <b>No students found.</b>
                <br />
                Create a new student or reach out to our{' '}
                <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                  Help Center
                </a>{' '}
                to bulk roster students.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassWizardStudentsStep;
